import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import type { Document, StoredDocument } from '@pleased-npm/fe-customisation';
import { createDocumentMap, parseStoredDocument } from '../utils/document.utils';
import { getHydratedThemeForRegistry } from '../utils/registryLoader';
import { dehydrateDocument } from '@pleased-npm/fe-customisation';
import { devLog } from '@/common/debug';

export const useCustomisationStore = defineStore('customisation', () => {
  const documents = ref<Document[]>([]);
  /** Flag indicating if documents were set from mocked/provided data instead of API fetch */
  const isMocked = ref(false);

  const documentMap = computed(() => createDocumentMap(documents.value));

  const pageDocuments = computed((): Document[] => {
    return documents.value.filter((doc) => doc.type === 'PAGE');
  });

  const themeDocuments = computed((): Document[] => {
    return documents.value.filter((doc) => doc.type === 'THEME');
  });

  const activeTheme = computed((): Document | null => {
    return documents.value.find((doc) => doc.type === 'THEME' && doc.active) || null;
  });

  function getDocumentByRegistryId(registryId: string): Document | undefined {
    return documentMap.value.get(registryId);
  }

  function setDocuments(newDocuments: Document[]) {
    documents.value = newDocuments;
  }

  function setDocument(document: Document) {
    const index = documents.value.findIndex((doc) => doc.id === document.id);
    if (index !== -1) {
      documents.value[index] = document;
    } else {
      documents.value.push(document);
    }
  }

  function clearAll() {
    documents.value = [];
    isMocked.value = false;
  }

  /**
   * Set mocked/provided documents from parent initialization.
   * Parses StoredDocument[] to Document[] and marks as mocked to skip API fetching.
   */
  function setMockedDocuments(storedDocuments: StoredDocument[]) {
    const parsedDocuments = storedDocuments.map(parseStoredDocument);
    documents.value = parsedDocuments;
    isMocked.value = true;
  }

  /**
   * Patch mocked documents with real-time updates from parent.
   * Merges by registryId: replaces existing documents with the same registryId, appends new ones.
   * If not currently in mocked mode, replaces all documents with the patch set and sets isMocked.
   */
  function patchMockedDocuments(storedDocuments: StoredDocument[]) {
    const parsedDocuments = storedDocuments.map(parseStoredDocument);
    if (!isMocked.value) {
      documents.value = parsedDocuments;
      isMocked.value = true;
      return;
    }
    const byRegistryId = new Map(documents.value.map((doc) => [doc.registryId, doc]));
    for (const doc of parsedDocuments) {
      byRegistryId.set(doc.registryId, doc);
    }
    documents.value = Array.from(byRegistryId.values());
  }

  /**
   * Builds and sets a default theme from registry if no theme exists.
   * This ensures there's always a theme available for the application.
   */
  const ensureDefaultTheme = () => {
    // Build default theme if no theme documents exist at all
    if (themeDocuments.value.length === 0) {
      devLog.debug('[CUSTOMISATION_STORE] No theme documents found, building default theme from registry');

      // Build default theme from registry
      const hydratedTheme = getHydratedThemeForRegistry(null);
      const defaultTheme = dehydrateDocument(hydratedTheme);

      // Add it to documents
      documents.value.push(defaultTheme);

      devLog.debug('[CUSTOMISATION_STORE] Default theme created and added to documents', defaultTheme);
    }
  };

  // Watch documents and automatically ensure default theme exists
  watch(
    documents,
    () => {
      ensureDefaultTheme();
    },
    { deep: true }
  );

  return {
    documents,
    isMocked,
    getDocumentByRegistryId,
    pageDocuments,
    themeDocuments,
    activeTheme,
    setDocuments,
    setDocument,
    clearAll,
    setMockedDocuments,
    patchMockedDocuments,
  };
});

export type CustomisationStore = ReturnType<typeof useCustomisationStore>;
